import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface Model {
  id: string;
  legalName: string;
  stageNames: string[] | string;
  dateOfBirth: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
  email?: string;
  verificationStatus: string;
}

interface IdDocument {
  id: string;
  documentType: string;
  verificationStatus: string;
  expirationDate?: string;
}

export default function ModelDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['model', id],
    queryFn: () =>
      api.get<{
        model: Model;
        connection: unknown;
        identificationDocuments: IdDocument[];
      }>(`/models/${id}`),
  });

  const unlinkMutation = useMutation({
    mutationFn: () => api.post(`/models/${id}/unlink`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      navigate('/models');
    },
  });

  if (isLoading) {
    return <div className="loading">Loading model...</div>;
  }

  const model = data?.data.model;
  const idDocs = data?.data.identificationDocuments || [];

  if (!model) {
    return <div className="error">Model not found</div>;
  }

  const stageNames = Array.isArray(model.stageNames)
    ? model.stageNames.join(', ')
    : model.stageNames;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{model.legalName}</h1>
          <p className="page-subtitle">{stageNames || 'No stage names'}</p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => {
              if (window.confirm(`Are you sure you want to unlink ${model.legalName}? This will remove them from your studio's connected models.`)) {
                unlinkMutation.mutate();
              }
            }}
            className="btn btn-danger"
            disabled={unlinkMutation.isPending}
          >
            Unlink Model
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="card">
          <h3>Personal Information</h3>
          <dl className="detail-list">
            <dt>Legal Name</dt>
            <dd>{model.legalName}</dd>

            <dt>Date of Birth</dt>
            <dd>{model.dateOfBirth}</dd>

            <dt>Email</dt>
            <dd>{model.email || '-'}</dd>

            <dt>Phone</dt>
            <dd>{model.phone || '-'}</dd>
          </dl>
        </div>

        <div className="card">
          <h3>Address</h3>
          <dl className="detail-list">
            <dt>Street</dt>
            <dd>
              {model.addressLine1}
              {model.addressLine2 && <br />}
              {model.addressLine2}
            </dd>

            <dt>City</dt>
            <dd>{model.city}</dd>

            <dt>State</dt>
            <dd>{model.state}</dd>

            <dt>Postal Code</dt>
            <dd>{model.postalCode}</dd>

            <dt>Country</dt>
            <dd>{model.country}</dd>
          </dl>
        </div>

        <div className="card">
          <h3>Verification Status</h3>
          <span
            className={`badge badge-large ${model.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}
          >
            {model.verificationStatus}
          </span>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Identification Documents</h3>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn btn-secondary"
          >
            Upload ID
          </button>
        </div>

        {idDocs.length === 0 ? (
          <p className="empty-text">No identification documents uploaded</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Expiration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {idDocs.map((doc) => (
                <tr key={doc.id}>
                  <td>{doc.documentType}</td>
                  <td>
                    <span
                      className={`badge ${doc.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}
                    >
                      {doc.verificationStatus}
                    </span>
                  </td>
                  <td>{doc.expirationDate || '-'}</td>
                  <td>
                    <button
                      className="btn btn-small"
                      onClick={() => {
                        // Open ID document in new tab for viewing
                        window.open(`/api/models/${id}/ids/${doc.id}/view`, '_blank');
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showUploadModal && (
        <IdUploadModal
          modelId={id!}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            queryClient.invalidateQueries({ queryKey: ['model', id] });
          }}
        />
      )}
    </div>
  );
}

function IdUploadModal({
  modelId,
  onClose,
  onSuccess,
}: {
  modelId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [documentType, setDocumentType] = useState('drivers_license');
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!frontImage) {
      setError('Front image is required');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('frontImage', frontImage);
      if (backImage) {
        formData.append('backImage', backImage);
      }
      if (expirationDate) {
        formData.append('expirationDate', expirationDate);
      }

      await api.upload(`/models/${modelId}/ids`, formData);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Identification</h2>
          <button onClick={onClose} className="modal-close">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="documentType">Document Type</label>
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              <option value="drivers_license">Driver's License</option>
              <option value="passport">Passport</option>
              <option value="state_id">State ID</option>
              <option value="military_id">Military ID</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="frontImage">Front Image *</label>
            <input
              type="file"
              id="frontImage"
              accept="image/*"
              onChange={(e) => setFrontImage(e.target.files?.[0] || null)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="backImage">Back Image</label>
            <input
              type="file"
              id="backImage"
              accept="image/*"
              onChange={(e) => setBackImage(e.target.files?.[0] || null)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="expirationDate">Expiration Date</label>
            <input
              type="date"
              id="expirationDate"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
